import * as React from 'react';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Seeded synchronously from the global window so the first paint already
 * reflects the preference. When `host` is set (for example a node inside an
 * authoring-preview iframe), the subscription moves to that node's window.
 * Guarded for SSR and for jsdom setups without `matchMedia`.
 */
export const useReducedMotion = (host?: Element | null): boolean => {
  const [reducedMotion, setReducedMotion] = React.useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(REDUCED_MOTION_QUERY).matches,
  );

  React.useEffect(() => {
    const view =
      host?.ownerDocument.defaultView ??
      (typeof window !== 'undefined' ? window : undefined);
    if (!view || typeof view.matchMedia !== 'function') return undefined;
    const query = view.matchMedia(REDUCED_MOTION_QUERY);
    const update = () => setReducedMotion(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, [host]);

  return reducedMotion;
};
